import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Loader2, RefreshCw, Search, X, AlertTriangle } from "lucide-react";
import toast from "react-hot-toast";

// ── Types ─────────────────────────────────────────────────────────────────────

interface DayEntry {
  date: string;
  estimatedHours: number;
  hoursUsed: number;
  actualHours?: number;
  clockInAt?: string;
  clockOutAt?: string;
  /** Timesheet Review Phase 2 status for this day. Absent = no review period covers this day
   *  (predates the feature, or this member isn't in the review chain) — rendered blank rather
   *  than "Pending" so HR isn't told something is stuck when nothing was ever tracked. */
  reviewStatus?: "pending" | "approved" | "anomaly_approved" | "anomaly_overridden";
  anomalyNote?: string;
}

interface StandardPayrollMember {
  id: string;
  name: string;
  employmentType: string;
  role?: string;
  timezone?: string;
  paymentMethod?: string;
  shiftStart: string;
  shiftEnd: string;
  breakMinutes: number;
  lunchTime?: string | null;
  hoursPerDay: number;
  hourlyRate: number;
  period1: { workingDays: number; hours: number; amount: number; payDate: string; days: DayEntry[] };
  period2: { workingDays: number; hours: number; amount: number; payDate: string; days: DayEntry[] };
}

interface UpworkPayrollMember {
  id: string;
  name: string;
  employmentType: string;
  role?: string;
  timezone?: string;
  shiftStart: string;
  shiftEnd: string;
  breakMinutes: number;
  lunchTime?: string | null;
  hoursPerDay: number;
  hourlyRate: number;
  weeks: Array<{ weekLabel: string; workingDays: number; hours: number; amount: number; payDate: string; days: DayEntry[] }>;
  total: number;
}

interface ReconciliationConfirmation {
  id: string;
  teamMemberId: string;
  name: string | null;
  periodKey: string;
  projectedAmount: number;
  actualAmount: number | null;
  payDate: string;
  isConfirmed: boolean;
  confirmedAt: string | null;
}

interface ReconciliationPM {
  balance: number;
  notes: string | null;
  projectedTotal: number;
  confirmedTotal: number;
  remainingBalance: number;
  confirmations: ReconciliationConfirmation[];
}

interface PayrollData {
  year: number;
  month: number;
  daysInMonth: number;
  period1: { label: string; total: number };
  period2: { label: string; total: number };
  members: StandardPayrollMember[];
  upwork: UpworkPayrollMember[];
  grandTotal: number;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<string, string> = {
  pcc: "PCC",
  team_lead: "Team Lead",
  operations_manager: "Operations Manager",
  kpi: "KPI",
  upper_management: "Upper Management",
  qa: "QA",
};

const EMP_TYPE_STYLES: Record<string, string> = {
  "full-time":  "bg-blue-100 text-blue-700",
  "part-time":  "bg-yellow-100 text-yellow-700",
  "contractor": "bg-purple-100 text-purple-700",
};

const PM_STYLES: Record<string, string> = {
  quickbooks: "bg-green-100 text-green-700",
  payoneer:   "bg-orange-100 text-orange-700",
  wise:       "bg-teal-100 text-teal-700",
  paypal:     "bg-blue-100 text-blue-700",
  veem:       "bg-violet-100 text-violet-700",
  upwork:     "bg-emerald-100 text-emerald-700",
};

const PM_LABELS: Record<string, string> = {
  quickbooks: "QuickBooks",
  payoneer:   "Payoneer",
  wise:       "Wise",
  paypal:     "PayPal",
  veem:       "Veem",
  upwork:     "Upwork",
  __none__:   "No Payment Method",
};

const PM_ORDER = ["quickbooks", "wise", "payoneer", "paypal", "veem", "__none__"];

// ── Helpers ───────────────────────────────────────────────────────────────────

async function apiFetch(path: string, options?: RequestInit) {
  const token = localStorage.getItem("authToken");
  const res = await fetch(`/api/v1${path}`, {
    cache: "no-store",
    ...options,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Request failed");
  return json.data;
}

function fmtPayDate(dateStr: string): string {
  const [y, mo, d] = dateStr.split("-").map(Number);
  return new Date(y, mo - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtTime(hhmm: string): string {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, "0")} ${ampm}`;
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function PayrollPage() {
  const today = new Date();
  const [year,  setYear]  = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data,  setData]  = useState<PayrollData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [collapsed,       setCollapsed]       = useState<Set<string>>(new Set());
  const [expandedMembers, setExpandedMembers] = useState<Set<string>>(new Set());
  const [searchQuery,     setSearchQuery]     = useState("");
  const [recon,           setRecon]           = useState<Record<string, ReconciliationPM>>({});

  const MONTH_NAMES = ["January","February","March","April","May","June",
                       "July","August","September","October","November","December"];

  const toggleSection = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const toggleMember = (id: string) =>
    setExpandedMembers((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const loadRecon = useCallback(async (y: number, m: number) => {
    try {
      const result = await apiFetch(`/cash-flow/payroll-reconciliation?year=${y}&month=${m}`);
      setRecon(result as Record<string, ReconciliationPM>);
    } catch { /* reconciliation is non-critical — silently ignore */ }
  }, []);

  const load = useCallback(async (y: number, m: number) => {
    setLoading(true);
    setError(null);
    try {
      const [result] = await Promise.all([
        apiFetch(`/payroll/payroll-projections?year=${y}&month=${m}`),
        loadRecon(y, m),
      ]);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load payroll projections");
    } finally {
      setLoading(false);
    }
  }, [loadRecon]);

  useEffect(() => { void load(year, month); }, [load, year, month]);

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };

  const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

  // Review status pill for the Timesheet Review Phase 2 chain — HR only needs to know whether a
  // day is fully finalized, and if it was flagged as an anomaly, whether the OM approved it as-is
  // or overrode it, with the reasoning available on hover. No "With PCC/TL/OM" breakdown here —
  // that level of detail belongs to the reviewers themselves (Team Lead / OM pages), not HR.
  const ReviewStatusPill = ({ status, note }: { status?: DayEntry["reviewStatus"]; note?: string }) => {
    if (!status) return null;
    const map: Record<NonNullable<DayEntry["reviewStatus"]>, { label: string; cls: string }> = {
      pending: { label: "Pending Review", cls: "bg-gray-100 text-gray-500" },
      approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-700" },
      anomaly_approved: { label: "Anomaly — Approved", cls: "bg-amber-100 text-amber-800" },
      anomaly_overridden: { label: "Anomaly — Overridden", cls: "bg-amber-100 text-amber-800" },
    };
    const { label, cls } = map[status];
    const isAnomaly = status === "anomaly_approved" || status === "anomaly_overridden";
    return (
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium whitespace-nowrap ${cls}`}
        title={isAnomaly ? note : undefined}
      >
        {isAnomaly && <AlertTriangle className="h-3 w-3" />}
        {label}
      </span>
    );
  };

  // ── Reconciliation panel — balance input + stats bar (per payment method) ──
  const ReconciliationPanel = ({ pmKey, projectedTotal }: { pmKey: string; projectedTotal: number }) => {
    const pmRecon = recon[pmKey];
    const balance        = pmRecon?.balance       ?? 0;
    const confirmedTotal = pmRecon?.confirmedTotal ?? 0;
    const remainingBal   = Math.max(0, balance - confirmedTotal);
    const netCashNeeded  = Math.max(0, projectedTotal - Math.max(0, balance) - confirmedTotal);

    const [balInput, setBalInput] = useState(balance.toFixed(2));
    const [saving,   setSaving]   = useState(false);

    useEffect(() => { setBalInput((pmRecon?.balance ?? 0).toFixed(2)); }, [pmRecon?.balance]);

    const saveBalance = async () => {
      const val = parseFloat(balInput);
      if (isNaN(val) || val < 0) { setBalInput(balance.toFixed(2)); return; }
      setSaving(true);
      try {
        await apiFetch("/cash-flow/payroll-reconciliation/balance", {
          method: "PUT",
          body: JSON.stringify({ paymentMethod: pmKey, year, month, balance: val }),
        });
        await loadRecon(year, month);
      } catch { toast.error("Failed to save balance"); setBalInput(balance.toFixed(2)); }
      finally  { setSaving(false); }
    };

    return (
      <div className="mb-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Account Balance</span>
          <div className="flex items-center gap-1">
            <span className="text-gray-400 text-xs">$</span>
            <input
              type="number" min="0" step="0.01"
              value={balInput}
              onChange={(e) => setBalInput(e.target.value)}
              onBlur={saveBalance}
              onKeyDown={(e) => e.key === "Enter" && saveBalance()}
              disabled={saving}
              className="w-28 text-sm font-semibold text-gray-800 border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:opacity-60"
            />
          </div>
        </div>
        <div className="text-xs text-gray-500">
          Confirmed Paid: <span className="font-semibold text-gray-700">{fmt(confirmedTotal)}</span>
        </div>
        <div className="text-xs text-gray-500">
          Remaining Balance: <span className="font-semibold text-gray-700">{fmt(remainingBal)}</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Net Cash Needed</span>
          <span className={`text-sm font-bold ${netCashNeeded > 0 ? "text-red-600" : "text-green-600"}`}>
            {fmt(netCashNeeded)}
          </span>
        </div>
      </div>
    );
  };

  // ── Confirm checkbox cell — per member per period ──────────────────────────
  const ConfirmCell = ({ member, pmKey, periodKey }: {
    member: StandardPayrollMember;
    pmKey: string;
    periodKey: "p1" | "p2";
  }) => {
    const period = periodKey === "p1" ? member.period1 : member.period2;
    const conf   = recon[pmKey]?.confirmations.find(
      (c) => c.teamMemberId === member.id && c.periodKey === periodKey
    );
    const isConfirmed = conf?.isConfirmed ?? false;

    const [editing,      setEditing]      = useState(false);
    const [actualInput,  setActualInput]  = useState((conf?.actualAmount ?? period.amount).toFixed(2));
    const [saving,       setSaving]       = useState(false);

    useEffect(() => {
      setActualInput((conf?.actualAmount ?? period.amount).toFixed(2));
    }, [conf?.actualAmount, period.amount]);

    const doConfirm = async (checked: boolean, amt?: number) => {
      setSaving(true);
      try {
        await apiFetch("/cash-flow/payroll-reconciliation/confirm", {
          method: "POST",
          body: JSON.stringify({
            teamMemberId:    member.id,
            paymentMethod:   pmKey,
            year, month,
            periodKey,
            projectedAmount: period.amount,
            payDate:         period.payDate,
            isConfirmed:     checked,
            actualAmount:    checked ? (amt ?? parseFloat(actualInput)) : undefined,
          }),
        });
        await loadRecon(year, month);
        if (checked) setEditing(false);
      } catch { toast.error("Failed to update payment"); }
      finally  { setSaving(false); }
    };

    return (
      <div className="flex flex-col items-end gap-0.5 mt-1.5">
        <label className="flex items-center gap-1.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isConfirmed}
            disabled={saving}
            onChange={(e) => { if (e.target.checked) setEditing(true); else doConfirm(false); }}
            className="h-3.5 w-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
          />
          <span className={`text-xs ${isConfirmed ? "text-green-600 font-medium" : "text-gray-400"}`}>
            {isConfirmed ? "Paid" : "Mark paid"}
          </span>
        </label>
        {(editing || isConfirmed) && (
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-400">$</span>
            <input
              type="number" min="0" step="0.01"
              value={actualInput}
              onChange={(e) => setActualInput(e.target.value)}
              disabled={saving || (isConfirmed && !editing)}
              onClick={() => isConfirmed && setEditing(true)}
              className="w-20 text-xs border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-green-400 disabled:bg-transparent disabled:border-transparent disabled:text-green-700 disabled:font-medium cursor-pointer"
            />
            {editing && (
              <>
                <button
                  onClick={() => doConfirm(true, parseFloat(actualInput))}
                  disabled={saving}
                  className="text-xs px-1.5 py-0.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >✓</button>
                <button onClick={() => { setEditing(false); setActualInput((conf?.actualAmount ?? period.amount).toFixed(2)); }}
                  className="text-xs text-gray-400 hover:text-gray-600">✕</button>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Per-member hours breakdown row ─────────────────────────────────────────
  const HoursBreakdownRow = ({ member, colSpan }: { member: StandardPayrollMember; colSpan: number }) => {
    const todayStr = new Date().toISOString().slice(0, 10);

    const daySource = (d: DayEntry): { label: string; cls: string } => {
      if (d.actualHours !== undefined && d.actualHours > 0) return { label: "· Clocked",   cls: "text-green-600" };
      if (d.actualHours === 0)                               return { label: "· No Entry",  cls: "text-red-400"   };
      if (d.date > todayStr)                                 return { label: "· Projected", cls: "text-blue-500"  };
      return                                                        { label: "· Estimated", cls: "text-gray-400"  };
    };

    const fmtClockTime = (iso: string, tz?: string): string =>
      new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: tz || "America/Los_Angeles" });

    const PeriodDays = ({ days, label, headerCls, tz, rate }: { days: DayEntry[]; label: string; headerCls: string; tz?: string; rate: number }) => {
      const totalSched  = days.reduce((s, d) => s + d.estimatedHours, 0);
      const totalActual = days.reduce((s, d) => s + (d.actualHours !== undefined ? d.actualHours : d.estimatedHours), 0);
      const totalDiffH   = days.reduce((s, d) => s + (d.actualHours !== undefined ? d.actualHours - d.estimatedHours : 0), 0);
      const totalDiffAmt = totalDiffH * rate;
      return (
        <div>
          <div className={`text-xs font-semibold mb-1.5 ${headerCls}`}>{label}</div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 border-b border-gray-100">
                <th className="text-left pb-1 pr-3 font-medium">Date</th>
                <th className="text-right pb-1 pr-3 font-medium">Sched</th>
                <th className="text-right pb-1 pr-3 font-medium">Actual</th>
                <th className="text-right pb-1 pr-3 font-medium">Diff</th>
                <th className="text-right pb-1 pr-3 font-medium">Source</th>
                <th className="text-right pb-1 font-medium">Review</th>
              </tr>
            </thead>
            <tbody>
              {days.map((d) => {
                const src = daySource(d);
                const [dy, dm, dd] = d.date.split("-").map(Number);
                const lbl = new Date(dy, dm - 1, dd).toLocaleDateString("en-US", { month: "short", day: "numeric" });
                const hasDiff = d.actualHours !== undefined;
                const diffH   = hasDiff ? d.actualHours! - d.estimatedHours : null;
                const diffAmt = diffH !== null ? diffH * rate : null;
                const diffCls = diffH === null ? "" : diffH < 0 ? "text-red-500" : diffH > 0 ? "text-amber-500" : "text-emerald-600";
                return (
                  <tr key={d.date} className="border-b border-gray-50 hover:bg-white/60">
                    <td className="py-0.5 pr-3 text-gray-700">{lbl}</td>
                    <td className="text-right pr-3 text-gray-400">{d.estimatedHours}h</td>
                    <td className="text-right pr-3 font-medium text-gray-700">
                      {d.actualHours !== undefined ? (
                        d.actualHours > 0 ? (
                          <span>
                            {d.actualHours}h
                            {d.clockInAt && d.clockOutAt && (
                              <span className="ml-1 text-gray-400 font-normal">
                                ({fmtClockTime(d.clockInAt, tz)} – {fmtClockTime(d.clockOutAt, tz)})
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-red-400">0h</span>
                        )
                      ) : (
                        <span>{d.hoursUsed}h</span>
                      )}
                    </td>
                    <td className={`text-right pr-3 font-medium ${diffCls}`}>
                      {diffH !== null ? (
                        diffH === 0 ? <span className="text-emerald-600">On schedule</span> : (
                          <span>
                            {diffH > 0 ? "+" : ""}{diffH.toFixed(1)}h
                            <span className="ml-1 font-normal">
                              ({diffH > 0 ? "+" : "-"}${Math.abs(diffAmt!).toFixed(2)})
                            </span>
                          </span>
                        )
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className={`text-right pr-3 ${src.cls}`}>{src.label}</td>
                    <td className="text-right">
                      <ReviewStatusPill status={d.reviewStatus} note={d.anomalyNote} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200 font-semibold text-gray-700">
                <td className="pt-1">Total</td>
                <td className="text-right pr-3 text-gray-400 pt-1">{totalSched.toFixed(1)}h</td>
                <td className="text-right pr-3 pt-1">{totalActual.toFixed(1)}h</td>
                <td className={`text-right pr-3 pt-1 ${totalDiffH < 0 ? "text-red-500" : totalDiffH > 0 ? "text-amber-500" : "text-emerald-600"}`}>
                  {totalDiffH === 0 ? <span className="text-emerald-600">On schedule</span> : (
                    <span>
                      {totalDiffH > 0 ? "+" : ""}{totalDiffH.toFixed(1)}h
                      <span className="ml-1 font-normal">
                        ({totalDiffH > 0 ? "+" : "-"}${Math.abs(totalDiffAmt).toFixed(2)})
                      </span>
                    </span>
                  )}
                </td>
                <td />
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      );
    };

    const hasDays = member.period1.days.length > 0 || member.period2.days.length > 0;
    if (!hasDays) return null;

    return (
      <tr className="bg-indigo-50/30 border-b border-indigo-100">
        <td colSpan={colSpan} className="px-5 py-3">
          <div className={`grid gap-8 ${member.period1.days.length > 0 && member.period2.days.length > 0 ? "grid-cols-2" : "grid-cols-1 max-w-sm"}`}>
            {member.period1.days.length > 0 && (
              <PeriodDays days={member.period1.days} label={`Period 1 — ${member.period1.days.length} day${member.period1.days.length !== 1 ? "s" : ""}`} headerCls="text-blue-600" tz={member.timezone} rate={member.hourlyRate} />
            )}
            {member.period2.days.length > 0 && (
              <PeriodDays days={member.period2.days} label={`Period 2 — ${member.period2.days.length} day${member.period2.days.length !== 1 ? "s" : ""}`} headerCls="text-indigo-600" tz={member.timezone} rate={member.hourlyRate} />
            )}
          </div>
        </td>
      </tr>
    );
  };

  // ── Standard bi-monthly table ──────────────────────────────────────────────
  const StandardTable = ({ members, period1Label, period2Label, pmKey }: {
    members: StandardPayrollMember[];
    period1Label: string;
    period2Label: string;
    pmKey: string;
  }) => {
    const p1Total = members.reduce((s, m) => s + m.period1.amount, 0);
    const p2Total = members.reduce((s, m) => s + m.period2.amount, 0);
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="w-8 px-2" rowSpan={2} />
                <th className="px-5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide" rowSpan={2}>Name / Schedule</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide" rowSpan={2}>Type</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wide" rowSpan={2}>Rate</th>
                <th colSpan={3} className="px-3 py-2 text-center text-xs font-semibold text-blue-600 uppercase tracking-wide border-l border-gray-200">
                  Period 1 ({period1Label})
                </th>
                <th colSpan={3} className="px-3 py-2 text-center text-xs font-semibold text-indigo-600 uppercase tracking-wide border-l border-gray-200">
                  Period 2 ({period2Label})
                </th>
                <th className="px-5 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wide border-l border-gray-200" rowSpan={2}>Monthly Total</th>
              </tr>
              <tr className="border-b border-gray-200 bg-gray-50/80 text-xs text-gray-400 font-medium uppercase tracking-wide">
                <th className="px-3 py-2 text-right border-l border-gray-200">Days</th>
                <th className="px-3 py-2 text-right">Paid Hrs</th>
                <th className="px-3 py-2 text-right">Pay Date · Amount</th>
                <th className="px-3 py-2 text-right border-l border-gray-200">Days</th>
                <th className="px-3 py-2 text-right">Paid Hrs</th>
                <th className="px-3 py-2 text-right">Pay Date · Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {members.map((m) => {
                const isExpanded = expandedMembers.has(m.id);
                const hasDays = m.period1.days.length > 0 || m.period2.days.length > 0;
                return (
                  <React.Fragment key={m.id}>
                    <tr className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-2 py-3 text-center">
                        {hasDays && (
                          <button
                            onClick={() => toggleMember(m.id)}
                            className="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                          </button>
                        )}
                      </td>
                      <td className="px-5 py-3 min-w-[220px]">
                        <div className="font-medium text-gray-900">{m.name}</div>
                        {m.role && <div className="text-xs text-gray-400 mt-0.5">{ROLE_LABELS[m.role] ?? m.role}</div>}
                        <div className="text-xs text-gray-400 mt-0.5">
                          {m.hoursPerDay > 0 ? (
                            <>
                              {fmtTime(m.shiftStart)} – {fmtTime(m.shiftEnd)}
                              {m.lunchTime && ` · 30 min lunch (unpaid)`}
                              {m.breakMinutes > 0 && ` · ${m.breakMinutes} min break (paid)`}
                              {` · ${m.hoursPerDay} paid hrs/day`}
                            </>
                          ) : (
                            <>Per-day schedule · {(m.period1.hours + m.period2.hours).toFixed(1)} paid hrs/mo</>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${EMP_TYPE_STYLES[m.employmentType] ?? "bg-gray-100 text-gray-600"}`}>
                          {m.employmentType}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right text-gray-700 whitespace-nowrap">{fmt(m.hourlyRate)}/hr</td>
                      <td className="px-3 py-3 text-right text-gray-700 border-l border-gray-100">{m.period1.workingDays}</td>
                      <td className="px-3 py-3 text-right text-gray-700">{m.period1.hours}</td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <div className="text-xs text-gray-400">{fmtPayDate(m.period1.payDate)}</div>
                        <div className="font-semibold text-gray-900">{fmt(m.period1.amount)}</div>
                        <ConfirmCell member={m} pmKey={pmKey} periodKey="p1" />
                      </td>
                      <td className="px-3 py-3 text-right text-gray-700 border-l border-gray-100">{m.period2.workingDays}</td>
                      <td className="px-3 py-3 text-right text-gray-700">{m.period2.hours}</td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <div className="text-xs text-gray-400">{fmtPayDate(m.period2.payDate)}</div>
                        <div className="font-semibold text-gray-900">{fmt(m.period2.amount)}</div>
                        <ConfirmCell member={m} pmKey={pmKey} periodKey="p2" />
                      </td>
                      <td className="px-5 py-3 text-right font-bold text-gray-900 border-l border-gray-100 whitespace-nowrap">
                        {fmt(m.period1.amount + m.period2.amount)}
                      </td>
                    </tr>
                    {isExpanded && <HoursBreakdownRow member={m} colSpan={11} />}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200 bg-gray-50 text-sm font-semibold text-gray-700">
                <td colSpan={4} className="px-5 py-3">Subtotal</td>
                <td colSpan={3} className="px-3 py-3 text-right border-l border-gray-200">
                  <span className="text-blue-700">{fmt(p1Total)}</span>
                </td>
                <td colSpan={3} className="px-3 py-3 text-right border-l border-gray-200">
                  <span className="text-indigo-700">{fmt(p2Total)}</span>
                </td>
                <td className="px-5 py-3 text-right text-base font-bold text-gray-900 border-l border-gray-200">
                  {fmt(p1Total + p2Total)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  // ── Upwork card ────────────────────────────────────────────────────────────
  const UpworkCard = ({ member }: { member: UpworkPayrollMember }) => (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="bg-emerald-50 border-b border-emerald-200 px-5 py-3 flex items-center justify-between">
        <div>
          <span className="text-sm font-semibold text-gray-800">{member.name}</span>
          {member.role && <span className="ml-2 text-xs text-gray-500">{ROLE_LABELS[member.role] ?? member.role}</span>}
          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">upwork</span>
          <div className="text-xs text-gray-400 mt-0.5">
            {member.hoursPerDay > 0 ? (
              <>
                {fmtTime(member.shiftStart)} – {fmtTime(member.shiftEnd)}
                {member.lunchTime && ` · 30 min lunch (unpaid)`}
                {member.breakMinutes > 0 && ` · ${member.breakMinutes} min break (paid)`}
                {` · ${member.hoursPerDay} paid hrs/day`}
              </>
            ) : (
              <>Per-day schedule · {member.weeks.reduce((s, w) => s + w.hours, 0).toFixed(1)} paid hrs/mo</>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-400">Monthly total</div>
          <div className="text-sm font-semibold text-gray-800">{fmt(member.total)}</div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="px-5 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Week</th>
              <th className="px-3 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Days</th>
              <th className="px-3 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Hours</th>
              <th className="px-3 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Pay Date</th>
              <th className="px-5 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {member.weeks.map((w, i) => (
              <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                <td className="px-5 py-3 text-gray-800">{w.weekLabel}</td>
                <td className="px-3 py-3 text-right text-gray-700">{w.workingDays}</td>
                <td className="px-3 py-3 text-right text-gray-700">{w.hours}</td>
                <td className="px-3 py-3 text-right text-gray-600 text-xs">{fmtPayDate(w.payDate)}</td>
                <td className="px-5 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">{fmt(w.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-200 bg-gray-50">
              <td colSpan={4} className="px-5 py-3 text-sm font-semibold text-gray-700">Total</td>
              <td className="px-5 py-3 text-right text-base font-bold text-gray-900">{fmt(member.total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  const q = searchQuery.toLowerCase().trim();
  const filteredMembers = data?.members.filter((m) => m.name.toLowerCase().includes(q)) ?? [];
  const filteredUpwork  = data?.upwork.filter((m)  => m.name.toLowerCase().includes(q)) ?? [];

  return (
    <div className="space-y-6">
      {/* Month navigator + search — sticky so Refresh stays reachable without scrolling up */}
      <div className="sticky top-16 z-10 flex items-center gap-3 bg-gray-50 py-2 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 border-b border-gray-200">
        <button onClick={prevMonth} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 transition">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-base font-semibold text-gray-800 w-44 text-center">
          {MONTH_NAMES[month - 1]} {year}
        </span>
        <button onClick={nextMonth} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 transition">
          <ChevronRight className="h-4 w-4" />
        </button>
        <div className="relative ml-4 flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search by name…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-md border border-gray-300 pl-8 pr-8 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <button
          onClick={() => void load(year, month)}
          disabled={loading}
          title="Refresh data for this month — keeps your current search and month selection"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {loading && !data && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          Loading projections…
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {!error && data && (
        <>
          {/* Grand total banner */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-emerald-700">Total Projected Payroll — {MONTH_NAMES[month - 1]} {year}</div>
              <div className="text-xs text-emerald-500 mt-0.5">
                {filteredMembers.length + filteredUpwork.length} hourly member{(filteredMembers.length + filteredUpwork.length) !== 1 ? "s" : ""}
                {filteredUpwork.length > 0 && ` (${filteredUpwork.length} Upwork)`}
                {q && ` matching "${searchQuery}"`}
              </div>
            </div>
            <div className="text-2xl font-bold text-emerald-700">{fmt(data.grandTotal)}</div>
          </div>

          {filteredMembers.length === 0 && filteredUpwork.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              {q ? `No members found matching "${searchQuery}".` : "No active hourly team members with a schedule and rate configured."}
            </div>
          ) : (
            <>
              {/* Standard bi-monthly — grouped by payment method */}
              {filteredMembers.length > 0 && (() => {
                const groupMap = new Map<string, StandardPayrollMember[]>();
                for (const m of filteredMembers) {
                  const key = m.paymentMethod ?? "__none__";
                  if (!groupMap.has(key)) groupMap.set(key, []);
                  groupMap.get(key)!.push(m);
                }
                const groups: Array<{ key: string; members: StandardPayrollMember[] }> = [];
                for (const pm of PM_ORDER) {
                  if (groupMap.has(pm)) groups.push({ key: pm, members: groupMap.get(pm)! });
                }
                for (const [k, v] of groupMap) {
                  if (!PM_ORDER.includes(k)) groups.push({ key: k, members: v });
                }
                return (
                  <div className="space-y-4">
                    {groups.map(({ key, members: gm }) => {
                      const label = PM_LABELS[key] ?? key;
                      const groupTotal = gm.reduce((s, m) => s + m.period1.amount + m.period2.amount, 0);
                      const isCollapsed = collapsed.has(key);
                      return (
                        <div key={key}>
                          <button
                            onClick={() => toggleSection(key)}
                            className="w-full flex items-center gap-2 mb-2 group"
                          >
                            <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${isCollapsed ? "" : "rotate-90"}`} />
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${PM_STYLES[key] ?? "bg-gray-100 text-gray-600"}`}>
                              {label}
                            </span>
                            <span className="text-xs text-gray-400">{gm.length} member{gm.length !== 1 ? "s" : ""}</span>
                            <span className="ml-auto text-sm font-semibold text-gray-700">{fmt(groupTotal)}</span>
                          </button>
                          {!isCollapsed && (
                            <>
                              <ReconciliationPanel pmKey={key} projectedTotal={groupTotal} />
                              <StandardTable members={gm} period1Label={data.period1.label} period2Label={data.period2.label} pmKey={key} />
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Upwork weekly section */}
              {filteredUpwork.length > 0 && (() => {
                const isCollapsed = collapsed.has("__upwork__");
                return (
                  <div>
                    <button
                      onClick={() => toggleSection("__upwork__")}
                      className="w-full flex items-center gap-2 mb-2 group"
                    >
                      <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${isCollapsed ? "" : "rotate-90"}`} />
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                        Upwork
                      </span>
                      <span className="text-xs text-gray-400">{filteredUpwork.length} member{filteredUpwork.length !== 1 ? "s" : ""} — Weekly Breakdown</span>
                      <span className="ml-auto text-sm font-semibold text-gray-700">
                        {fmt(filteredUpwork.reduce((s, u) => s + u.total, 0))}
                      </span>
                    </button>
                    {!isCollapsed && (
                      <div className="space-y-6">
                        {filteredUpwork.map((u) => <UpworkCard key={u.id} member={u} />)}
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </>
      )}
    </div>
  );
}
